import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useApiClient } from '@ooh/screens';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bell, CheckCheck } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@ooh/ui/components/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ooh/ui/components/tooltip';
import { Badge } from '@ooh/ui/components/badge';
import { cn } from '@ooh/ui/lib/utils';
import { useUnreadCount } from '../hooks/use-unread-count';
import { useMarkNotificationRead, useMarkAllNotificationsRead } from '../api/mutations';
import { listNotificationsQueryOptions } from '../api/queries';
import { NotificationItem } from './notification-item';
import type { Notification } from '@ooh/contracts';
import {
  RESERVATION_TTL_EXPIRING_EVENT_TYPE,
  RESERVATION_TTL_EXPIRED_EVENT_TYPE,
  RESERVATION_QUEUE_PROMOTED_EVENT_TYPE,
  OFFER_VERSION_ACCEPTED_EVENT_TYPE,
  OFFER_VERSION_REJECTED_EVENT_TYPE,
} from '@ooh/contracts';

// ─── Relative timestamp helper ────────────────────────────────

/**
 * Returns a human-readable relative time string using Intl.RelativeTimeFormat.
 * No external date library required — uses the native Intl API.
 *
 * @param date - the date to format
 * @param locale - the BCP 47 language tag (e.g. 'pl', 'en') from i18next
 */
function formatRelativeTime(date: Date, locale: string): string {
  const now = Date.now();
  const diffMs = date.getTime() - now;
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffSec) < 60) return rtf.format(diffSec, 'second');
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
  if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
  return rtf.format(diffDay, 'day');
}

// ─── Event type → i18n key mapping ───────────────────────────

type NotificationI18nKey =
  | 'notifications.ttlExpiring'
  | 'notifications.ttlExpired'
  | 'notifications.queuePromoted'
  | 'notifications.offerVersionAccepted'
  | 'notifications.offerVersionRejected';

const EVENT_TYPE_I18N_KEY: Record<string, NotificationI18nKey> = {
  [RESERVATION_TTL_EXPIRING_EVENT_TYPE]: 'notifications.ttlExpiring',
  [RESERVATION_TTL_EXPIRED_EVENT_TYPE]: 'notifications.ttlExpired',
  [RESERVATION_QUEUE_PROMOTED_EVENT_TYPE]: 'notifications.queuePromoted',
  [OFFER_VERSION_ACCEPTED_EVENT_TYPE]: 'notifications.offerVersionAccepted',
  [OFFER_VERSION_REJECTED_EVENT_TYPE]: 'notifications.offerVersionRejected',
};

// ─── NotificationBell ─────────────────────────────────────────

/**
 * Header notification bell with unread badge and dropdown list.
 *
 * Controlled DropdownMenu (open/setOpen) is required for programmatic close on navigation.
 * See: shadcn skill rule state-use-controlled-dialog-state.
 *
 * data-testid values:
 * - notification-bell — the trigger button
 * - notification-bell-badge — unread badge (only rendered when count > 0)
 * - notification-dropdown — the open dropdown container
 * - notification-item-{id} — each row (via NotificationItem)
 * - notification-mark-all-read — "Mark all as read" button
 *
 * Traceability: REQ-10.2 → reservation-reminder-in-app-notifications / spec.md:207–245
 */
export function NotificationBell() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const api = useApiClient();
  const [open, setOpen] = React.useState(false);

  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.unreadCount ?? 0;

  const { data: listData } = useQuery(listNotificationsQueryOptions(api));
  const notifications: Notification[] = (listData?.data as Notification[] | undefined) ?? [];

  const { mutateAsync: markRead } = useMarkNotificationRead();
  const { mutate: markAllRead } = useMarkAllNotificationsRead();

  // Badge label: cap at 99+
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);

  async function handleItemClick(notification: Notification) {
    try {
      await markRead(notification.id);
    } catch {
      // markRead rejection is intentionally swallowed: navigation proceeds regardless
      // and the optimistic unread count will roll back via onError in the mutation.
    }
    setOpen(false);
    const payload = notification.payload as Record<string, unknown>;
    if (payload.offerId != null) {
      void navigate({ to: '/offers/$offerId', params: { offerId: String(payload.offerId) } });
    } else if (payload.mediaUnitId != null) {
      void navigate({ to: '/media-units/$id', params: { id: String(payload.mediaUnitId) } });
    }
  }

  function handleMarkAllRead() {
    markAllRead();
  }

  function getNotificationTitle(notification: Notification): string {
    const i18nKey: NotificationI18nKey =
      EVENT_TYPE_I18N_KEY[notification.eventType] ?? 'notifications.ttlExpiring';
    return t(i18nKey, { count: notification.count });
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          data-testid="notification-bell"
          aria-label={t('notifications.bellLabel')}
          className="relative"
        >
          <Bell className="h-4 w-4" strokeWidth={1.8} />
          {unreadCount > 0 && (
            <Badge
              data-testid="notification-bell-badge"
              variant="destructive"
              className={cn(
                'absolute -top-1 -right-1 h-4 min-w-4 px-1 text-xs leading-none flex items-center justify-center rounded-full pointer-events-none',
              )}
            >
              {badgeLabel}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        data-testid="notification-dropdown"
        align="end"
        className="w-88 p-0 max-h-112 overflow-y-auto"
      >
        {/* Sticky header with icon-only mark-all-read action */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border/60 bg-popover px-3 py-2.5">
          <span className="text-sm font-semibold">{t('notifications.bellLabel')}</span>
          {notifications.length > 0 && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    data-testid="notification-mark-all-read"
                    onClick={handleMarkAllRead}
                    aria-label={t('notifications.markAllRead')}
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                  >
                    <CheckCheck className="h-4 w-4" strokeWidth={1.8} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('notifications.markAllRead')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Notification list or empty state */}
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            {t('notifications.emptyState')}
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                id={notification.id}
                title={getNotificationTitle(notification)}
                timestamp={formatRelativeTime(notification.createdAt, i18n.language)}
                isRead={notification.readAt !== null}
                onClick={() => {
                  void handleItemClick(notification);
                }}
              />
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
